import React, { useState, useRef } from 'react';
import { Icon, Popup } from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import { useFavorites, FavoriteItem } from './FavoritesContext';
import { CustomIcon } from '../icons';
import './FavoritesSection.css';

interface FavoritesSectionProps {
  currentPath: string;
  isMinimized: boolean;
}

export const FavoritesSection: React.FC<FavoritesSectionProps> = ({
  currentPath,
  isMinimized
}) => {
  const { favorites, removeFavorite, reorderFavorites } = useFavorites();
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const draggedItem = useRef<FavoriteItem | null>(null);

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    draggedItem.current = favorites[index];
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', '');
  };
  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    // Only update if different to reduce re-renders
    if (dragOverIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragEnter = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();

    if (draggedIndex === null || draggedItem.current === null) return;

    if (draggedIndex !== dropIndex) {
      const newFavorites = [...favorites];
      const draggedFavorite = newFavorites.splice(draggedIndex, 1)[0];
      newFavorites.splice(dropIndex, 0, draggedFavorite);

      reorderFavorites(newFavorites);
    }

    setDraggedIndex(null);
    setDragOverIndex(null);
    draggedItem.current = null;
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
    draggedItem.current = null;
  };

  const handleRemoveFavorite = (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    e.stopPropagation();
    removeFavorite(id);
  };
  if (favorites.length === 0) {
    return (<div className="favorites-section favorites-section-empty">
      <div className="favorites-header">
        <div className="favorites-header-content">
          <CustomIcon name="heart-01" className="favorites-header-icon" />
          {!isMinimized && (
            <span className="favorites-header-label">Favorites</span>
          )}
        </div>
      </div>
      {!isMinimized && (
        <div className="favorites-empty-message">
          <span>No favorites yet</span>
        </div>
      )}
    </div>
    );
  }
  return (
    <div className="favorites-section">
      {!isMinimized && (
        <div className="favorites-header">
          <div className="favorites-header-content">
            <CustomIcon name="heart-01" className="favorites-header-icon" />
            <span className="favorites-header-label">Favorites</span>
          </div>
        </div>
      )}
      <div className="favorites-list">
        {favorites.map((favorite, index) => (
          <div
            key={favorite.id}
            className={`favorite-item-wrapper ${draggedIndex === index ? 'dragging' : ''
              } ${dragOverIndex === index ? 'drag-over' : ''}`}
            draggable={!isMinimized}
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragEnter={(e) => handleDragEnter(e, index)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, index)}
            onDragEnd={handleDragEnd}
          >
            <Link
              to={favorite.url}
              className={`favorite-item ${currentPath === favorite.url ? 'active' : ''
                }`}
              draggable={false} // Prevent link from being draggable
            >
              {!isMinimized && (
                <div className="favorite-drag-handle">
                  <Icon name="ellipsis vertical" className="favorite-drag-icon" />
                </div>
              )}                <div className="favorite-content">
                {isMinimized && (
                  <Popup
                    content={favorite.name}
                    trigger={
                      <div className="favorite-icon">
                        <CustomIcon name="heart-01" className="favorite-icon-heart" />
                      </div>
                    }
                    position="right center"
                    inverted
                    size="mini"
                  />
                )}

                {!isMinimized && (
                  <span className="favorite-label" title={favorite.name}>
                    {favorite.name}
                  </span>
                )}
              </div>

              {!isMinimized && (
                <Popup
                  content="Remove from favorites"
                  trigger={
                    <div
                      className="favorite-remove"
                      onClick={(e) => handleRemoveFavorite(e, favorite.id)}
                    >
                      <Icon name="times" className="favorite-remove-icon" />
                    </div>
                  }
                  position="right center"
                  inverted
                  size="mini"
                />
              )}
            </Link>
          </div>
        ))}
      </div>

      {!isMinimized && (
        <div className="favorites-footer">
          <span className="favorites-count">
            {favorites.length}/10 favorites
          </span>
        </div>
      )}
    </div>
  );
};
